/**
 * SetPasswordPage — used by:
 *   1) OTP-first users (those who logged in via email-OTP and never picked a
 *      password). The "Set a password" banner on /ops/dashboard sends them here.
 *   2) Anyone changing their existing password (Settings menu).
 *
 * Auth: cookie session. The `current_password` field is only required when the
 * user already has a password on file (auth/me returns `has_password: true`).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useTrackOticAuth();

  const [hasPassword, setHasPassword] = useState(true);
  const [loadingProbe, setLoadingProbe] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // Probe /auth/me so we know whether to require current_password.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authApi.me();
        if (alive) setHasPassword(!!res.data?.user?.has_password);
      } catch {
        // If /me fails the user isn't authenticated — bounce home.
        navigate('/', { replace: true });
      } finally {
        if (alive) setLoadingProbe(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (hasPassword && !currentPassword) {
      toast.error('Enter your current password');
      return;
    }
    setSaving(true);
    try {
      await authApi.setPassword(hasPassword ? currentPassword : null, newPassword);
      toast.success(hasPassword ? 'Password changed' : 'Password set — you can now log in with email + password');
      await refreshUser?.();
      // OTP-first users go to the dashboard; password-change goes back to where they came from.
      navigate(user?.tenant_id ? '/ops/dashboard' : '/control/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save password');
    } finally {
      setSaving(false);
    }
  };

  if (loadingProbe) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
      <Card className="w-full max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
            <KeyRound className="w-5 h-5 text-blue-500" />
            {hasPassword ? 'Change your password' : 'Set your password'}
          </CardTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {hasPassword
              ? 'Pick a strong new password. You will still be able to log in via email OTP after this.'
              : "You're logged in via email OTP. Set a password so you can log in faster next time."}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
            {hasPassword && (
              <div>
                <Label htmlFor="current">Current password</Label>
                <Input
                  id="current"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="current-password"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="new-password"
                required
                minLength={6}
              />
              <p className="text-xs text-slate-500 mt-1">At least 6 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="confirm-password"
                required
              />
            </div>
            <Button type="submit" disabled={saving} data-testid="save-password-btn" className="w-full bg-blue-600 hover:bg-blue-700">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" />{hasPassword ? 'Change password' : 'Set password'}</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
